import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { useBusiness } from "../../lib/business.js";
import { useFeatures, useMe } from "../auth/api.js";
import styles from "./AiAgentScreen.module.css";
import {
  type AiSettingsPatch,
  estimateReplyMyr,
  useAiModels,
  useAiSettings,
  usePatchAiSettings,
} from "./api.js";

const myr = (n: number) => `RM${n.toFixed(2)}`;

/**
 * AI Agent screen (doc 13 §2/§7/§8) — the module's configuration surface,
 * mounted only when the deployment flag is on (direct navigation with the flag
 * off redirects to the Dashboard, cascade row 2). The live pipeline pieces
 * (KB sync, Test-the-agent) activate at module enablement; their cards say so.
 */
export function AiAgentScreen() {
  const features = useFeatures();
  const { activeId } = useBusiness();
  const me = useMe();
  const q = useAiSettings(activeId);
  const models = useAiModels();
  const patch = usePatchAiSettings(activeId);
  const canEdit = me.data?.role === "admin" || me.data?.role === "maintainer";

  // Draft state for the free-text fields (saved explicitly; the rest patch on change).
  const [persona, setPersona] = useState("");
  const [capDraft, setCapDraft] = useState("");
  const s = q.data?.settings;
  const u = q.data?.usage;
  useEffect(() => {
    if (s) {
      setPersona(s.persona);
      setCapDraft(String(Number(s.monthlyCapMyr)));
    }
  }, [s]);

  if (!features.isLoading && features.data && !features.data.aiModule) {
    return <Navigate to="/" replace />;
  }
  if (!s || !u) return <section className={styles.wrap}>Loading…</section>;

  const apply = (p: AiSettingsPatch) => patch.mutate(p);
  const cap = Number(s.monthlyCapMyr);
  const pct = cap > 0 ? Math.round((u.spentMyr / cap) * 100) : 0;

  return (
    <section className={styles.wrap}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>AI Agent</h1>
          <p className={styles.sub}>RAG knowledge base, confidence handoff &amp; usage budget</p>
        </div>
        <div className={styles.masterRow}>
          <span
            className={`${styles.statePill} ${s.enabled && !u.paused ? styles.activePill : ""}`}
          >
            {s.enabled ? (u.paused ? "BUDGET PAUSED" : "ACTIVE") : "PAUSED"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={s.enabled}
            aria-label="Toggle AI agent"
            disabled={!canEdit || patch.isPending}
            className={`${styles.switch} ${s.enabled ? styles.on : ""}`}
            onClick={() => apply({ enabled: !s.enabled })}
          >
            <span className={styles.knob} />
          </button>
        </div>
      </header>

      {u.paused && (
        <div className={styles.banner}>
          AI agent paused — monthly budget ({myr(cap)}) reached. Chats are routed to staff. Adjust
          the cap to resume.
        </div>
      )}

      <div className={styles.grid}>
        <article className={styles.card}>
          <h2 className={styles.cardTitle}>Usage budget</h2>
          <p className={styles.bigStat}>
            {myr(u.spentMyr)} <span className={styles.dim}>/ {myr(cap)}</span>
          </p>
          <div className={styles.barTrack}>
            <div
              className={`${styles.barFill} ${pct > 85 ? styles.barWarn : ""}`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <p className={styles.statLine}>
            {pct}% used · {u.replies} AI replies this month · avg {myr(u.avgCostMyr)}/reply
          </p>
          <p className={styles.statLine}>
            Projected month-end: {u.dayOfMonth < 3 ? "–" : myr(u.projectedMyr)}
          </p>
          {canEdit && (
            <div className={styles.capRow}>
              <label className={styles.fieldLabel} htmlFor="ai-cap">
                Monthly cap (RM)
              </label>
              <input
                id="ai-cap"
                className={styles.numInput}
                type="number"
                min={0}
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
              />
              <button
                type="button"
                className={styles.btn}
                disabled={patch.isPending || Number(capDraft) === cap}
                onClick={() => apply({ monthlyCapMyr: Number(capDraft) })}
              >
                Save
              </button>
            </div>
          )}
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={s.pauseAtCap}
              disabled={!canEdit || patch.isPending}
              onChange={(e) => apply({ pauseAtCap: e.target.checked })}
            />
            <span>
              Pause at cap — when spend hits {myr(cap)}, the bot stops and chats route to staff.
            </span>
          </label>
        </article>

        <article className={styles.card}>
          <h2 className={styles.cardTitle}>Model</h2>
          {(models.data ?? []).map((m) => (
            <label key={m.id} className={styles.modelRow}>
              <input
                type="radio"
                name="ai-model"
                checked={s.modelId === m.id}
                disabled={!canEdit || patch.isPending}
                onChange={() => apply({ modelId: m.id })}
              />
              <span className={styles.modelName}>{m.displayName}</span>
              {m.tag && <span className={styles.tag}>{m.tag}</span>}
              <span className={styles.modelCost}>≈{myr(estimateReplyMyr(m))}/reply</span>
              {m.note && <span className={styles.modelNote}>{m.note}</span>}
            </label>
          ))}
        </article>

        <article className={styles.card}>
          <h2 className={styles.cardTitle}>Behaviour</h2>
          <label className={styles.fieldLabel} htmlFor="ai-persona">
            Persona
          </label>
          <textarea
            id="ai-persona"
            className={styles.textarea}
            rows={4}
            value={persona}
            disabled={!canEdit}
            onChange={(e) => setPersona(e.target.value)}
          />
          {canEdit && (
            <button
              type="button"
              className={styles.btn}
              disabled={patch.isPending || persona === s.persona}
              onClick={() => apply({ persona })}
            >
              Save persona
            </button>
          )}
          <label className={styles.fieldLabel} htmlFor="ai-threshold">
            Hand to a human below confidence: <strong>{s.confidenceThreshold}%</strong>
          </label>
          <input
            id="ai-threshold"
            className={styles.slider}
            type="range"
            min={40}
            max={95}
            step={5}
            value={s.confidenceThreshold}
            disabled={!canEdit || patch.isPending}
            onChange={(e) => apply({ confidenceThreshold: Number(e.target.value) })}
          />
          <p className={styles.help}>
            Lower = the bot answers more on its own. Higher = it hands borderline questions to staff
            sooner.
          </p>
          <p className={styles.statLine}>
            Always handed to a human: {s.handoffIntents.join(", ") || "—"} · rollout stage:{" "}
            {s.rolloutStage}
          </p>
        </article>

        <article className={styles.card}>
          <h2 className={styles.cardTitle}>Knowledge base</h2>
          <p className={styles.empty}>
            No sources connected yet. Catalog sync (Shopify/Woo), policy documents and FAQ uploads
            are wired at module enablement.
          </p>
        </article>

        <article className={styles.card}>
          <h2 className={styles.cardTitle}>Test the agent</h2>
          <p className={styles.empty}>
            Available at module enablement — runs the live pipeline (real KB retrieval, real order
            lookup, real model) without sending anything to WhatsApp. Test replies count toward the
            monthly budget.
          </p>
        </article>
      </div>
    </section>
  );
}
